// Single source of truth for "benign browser noise" — client errors that carry
// NO actionable signal and must not count against EITHER error sink (Sentry OR
// the owned js_error beacon → events_raw, rule 34). Both sinks share this drop
// list so it can never drift (rules 23/24 "drifting copies").
//
// Verified 2026-06-26 from events_raw (the owned beacon's first 3 days): the
// SecurityError class alone was 166 events / 152 users — the 2nd-biggest error
// class — and 100% benign third-party churn:
//   • Stackless `SecurityError: "The request was denied."` — Google Tag Manager /
//     Google tags invoke a permission-gated API (navigator.share / clipboard /
//     fullscreen / vibrate / a Privacy-Sandbox call) in a denied context on
//     Google-referred mobile sessions. No frame, no URL → unfixable in any page
//     component; it is pure third-party noise (already dropped from Sentry by
//     sentry-pii-scrubber.ts, which shares the needle list below).
//   • Restricted-context SecurityError (localStorage / Web Locks API) from
//     private frames, embedded previews, or enterprise-policy sandboxes.
//   • Cross-origin `Script error.` — the browser masks the real error for CORS,
//     so it carries zero actionable detail (Sentry's default ignoreErrors drops
//     it too).

/** Restricted-context SecurityError messages — the shared needle list. */
export const RESTRICTED_BROWSER_API_NEEDLES = [
  "Failed to read the 'localStorage' property from 'Window'",
  "Failed to execute 'request' on 'LockManager'",
  "Access to the Locks API is denied",
] as const;

// A real stack frame names a call site: `at fn (url:line)`, `fn@url:line`, or a
// bare URL. A SecurityError whose "stack" is only the message line has none.
const STACK_FRAME_RE = /(\bat\s|@|https?:\/\/)/;

/** Whether a stack string contains at least one real call-site frame. */
export function hasStackFrames(stack: string | undefined): boolean {
  return Boolean(stack && STACK_FRAME_RE.test(stack));
}

/**
 * True for a SecurityError that is unactionable browser / third-party noise:
 * a stackless "request was denied" (GTM/Google permission-gated API) OR a
 * restricted-context storage / Web Locks API error. A SecurityError WITH real
 * frames still flows through — that one we can debug.
 */
export function isBenignSecurityError(name: string, message: string, hasFrames: boolean): boolean {
  if (name !== "SecurityError") return false;
  if (!hasFrames) return true;
  return RESTRICTED_BROWSER_API_NEEDLES.some((needle) => message.includes(needle));
}

// Browser-extension scheme prefixes. `webkit-masked-url://hidden/` is Safari's
// masking of extension-injected script URLs.
const EXTENSION_URL_RE =
  /^(?:chrome-extension|moz-extension|safari-extension|safari-web-extension|webkit-masked-url):\/\//i;

/**
 * True when every NAMED frame URL is a browser-extension scheme (and there is
 * at least one). Shared with the Sentry scrubber, which passes parsed
 * `frame.filename` values instead of a raw stack string.
 *
 * Verified 2026-07-15 from events_raw: `CustomError: Jsloader error (code #0):
 * Error while loading script https://apis.google.com/js/client.js?onload=…` on
 * /free-kundali/ — every frame lived in
 * `chrome-extension://…/tracking_script.js` (an extension loading Google's
 * gapi in OUR page context and failing to fetch it). Not fixable from any page
 * component; the extension owner's bugs must not count against the owned
 * error stream.
 */
export function isExtensionOnlyFrameUrls(urls: ReadonlyArray<string | undefined | null>): boolean {
  const named = urls.filter((u): u is string => Boolean(u));
  if (named.length === 0) return false;
  return named.every((u) => EXTENSION_URL_RE.test(u));
}

// A stack frame LINE (V8 `    at fn (url:line:col)` or Firefox/Safari
// `fn@url:line:col`) — scanned separately from the message line, because the
// message itself often embeds an https:// URL (e.g. the gapi Jsloader message
// names the script it failed to load) which must not defeat the check.
const FRAME_LINE_RE = /^\s*at\s|@/;
const FRAME_URL_RE = /[a-z][a-z+.-]*:\/\/[^\s)]+/gi;

/**
 * True when the stack's frame lines carry ONLY browser-extension URLs — the
 * error was raised and handled entirely inside an extension's content script.
 * A stack with ANY http(s) frame (our /_next/ chunks, any web script) still
 * flows through, so a real app error can never be suppressed by this class.
 */
export function isExtensionOnlyStack(stack: string | undefined): boolean {
  if (!stack) return false;
  const frameLines = stack.split("\n").filter((line) => FRAME_LINE_RE.test(line));
  const urls = frameLines.flatMap((line) => line.match(FRAME_URL_RE) ?? []);
  return isExtensionOnlyFrameUrls(urls);
}

export interface JsErrorNoiseInput {
  name?: string;
  message?: string;
  stack?: string;
}

/**
 * True when an owned `js_error` beacon should be DROPPED as benign noise, so the
 * events_raw error stream stays a signal of REAL, fixable app failures. Mirrors
 * the Sentry-side scrubber (sentry-pii-scrubber.ts) via the shared needle list.
 */
export function isBenignBrowserNoise(input: JsErrorNoiseInput): boolean {
  const name = input.name ?? "";
  const message = input.message ?? "";
  if (isBenignSecurityError(name, message, hasStackFrames(input.stack))) return true;
  // Cross-origin "Script error." — CORS-masked, zero actionable detail.
  if (message === "Script error." || message === "Script error") return true;
  // Pure-anonymous "Maximum call stack" (no URL frames) = a THIRD-PARTY recursion
  // such as Google's gtag.js Ok<->Mk GTM double-config loop — NOT our code (ours
  // always carries /_next/ chunk URLs in the stack). The real fix is single-owner
  // GA4 config (analytics-ga4.ts); this only keeps the residual third-party
  // recursion out of the owned stream. A real app recursion still flows through.
  if (message === "Maximum call stack size exceeded." && !/https?:\/\//.test(input.stack ?? "")) {
    return true;
  }
  // Errors raised + handled entirely inside a browser extension's content
  // script (every frame URL is an extension scheme). The 2026-07-15 class: a
  // Chrome extension's tracking_script.js loading Google's gapi client in our
  // page context and reporting the load failure through window.onerror.
  if (isExtensionOnlyStack(input.stack)) return true;
  return false;
}
